import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import './AppLoader.scss';
import { TimeLoaderFullscreen } from '../TimeLoaderFullscreen';
import { useIsDarkmode } from '../../hooks/useIsDarkmode';
import { HubConnection } from '@microsoft/signalr';
import { useEnvironment } from '../../Authentication/hooks/useEnvironment';

type OperationStatus = {
    name: string | null;
    description: string | null;
    ready: boolean;
    timeStamp: string;
};

const CacheStatus = () => {
    const [logMessages, setLogMessages] = useState<OperationStatus[]>([]);
    const hubConnection = useRef<HubConnection | null>(null);
    const logContainerRef = useRef<HTMLDivElement | null>(null);

    const stopConnection = useCallback(() => {
        if (hubConnection.current && hubConnection.current.state === signalR.HubConnectionState.Connected) {
            hubConnection.current.stop().then(() => {
                console.debug('SignalR connection stopped.');
            });
        }
    }, []);

    useEffect(() => {
        if (!hubConnection.current) {
            const connection = new signalR.HubConnectionBuilder().withUrl('/hubs/hoststatus').withAutomaticReconnect([0, 1000, 2000, 5000]).build();

            hubConnection.current = connection;

            // Register handler before starting so no messages are missed
            connection.on('onMessage', message => {
                setLogMessages(prevMessages => [...prevMessages, message]);
                if (
                    logContainerRef.current &&
                    logContainerRef.current.scrollHeight - logContainerRef.current.scrollTop !== logContainerRef.current.clientHeight
                ) {
                    logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
                }
            });

            const startConnection = () => {
                connection
                    .start()
                    .then(() => {
                        console.debug('SignalR connection established.');
                    })
                    .catch(err => {
                        console.debug('SignalR connection failed, retrying in 2s...', err);
                        setTimeout(startConnection, 2000);
                    });
            };

            startConnection();
        }
    }, []);

    useEffect(() => {
        return () => {
            stopConnection();
        };
    }, [stopConnection]);

    const isDarkmode = useIsDarkmode();

    return (
        <TimeLoaderFullscreen>
            <div style={{ marginTop: '32px', textAlign: 'left', maxHeight: '50vh', overflowY: 'auto', width: 800 }} ref={logContainerRef}>
                <ul style={{ listStyleType: 'none', padding: 0 }}>
                    {logMessages.map((message, index) => (
                        <li
                            key={index}
                            style={{
                                backgroundColor: isDarkmode ? '#2d2d2d' : '#f0f0f0',
                                color: isDarkmode ? '#e0e0e0' : '#1a1a1a',
                                padding: '10px',
                                borderRadius: '4px',
                                margin: '8px 0',
                                animation: 'fade-in 0.5s ease-in-out forwards',
                                animationDelay: `${index * 0.1}s`,
                                textAlign: 'left',
                            }}
                        >
                            <div>
                                <strong>Name:</strong> {message.name}
                            </div>
                            <div>
                                <strong>Description:</strong> {message.description}
                            </div>
                            <div>
                                <strong>Timestamp:</strong> {message.timeStamp}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </TimeLoaderFullscreen>
    );
};

const EnvironmentGate = ({ children }: { children: React.ReactNode }) => {
    useEnvironment(); // Suspends here until isReady=true, triggering AppLoader's CacheStatus fallback
    return <>{children}</>;
};

export const AppLoader = ({ children }: { children: React.ReactNode }) => {
    return (
        <Suspense fallback={<CacheStatus />}>
            <EnvironmentGate>{children}</EnvironmentGate>
        </Suspense>
    );
};
